import type { PayloadAction } from "@reduxjs/toolkit";
import { IContextFile } from "interface/common";
import { IVisualization } from "interface/component";
import GetSessionSummaryResponse from "model/api/GetSessionSummaryResponse";
import ConversationModel from "model/Conversation.model";
import MessageModel from "model/Message.model";
import { SessionTimelinePointInfo } from "model/SessionTimelinePointInfo.model";
import { SessionState } from "slice/SessionSlice";
import { mergeArray } from "util/CommonUtil";
import { FILE_STATUS } from "util/Constant";

export const setSelectedConversationReducer = (
  state: SessionState,
  action: PayloadAction<ConversationModel>
) => {
  return {
    ...state,
    selectedConversation: action.payload,
  };
};

export const setConversationListReducer = (
  state: SessionState,
  action: PayloadAction<ConversationModel[]>
): SessionState | void => {
  return {
    ...state,
    conversations: action.payload,
  };
};

export const mergeConversationListReducer = (
  state: SessionState,
  action: PayloadAction<ConversationModel[]>
): SessionState | void => {
  return {
    ...state,
    conversations: mergeArray(state.conversations, action.payload),
  };
};

export const addConversationToTheBeginningReducer = (
  state: SessionState,
  action: PayloadAction<ConversationModel>
): SessionState | void => {
  return {
    ...state,
    conversations: [action.payload, ...state.conversations],
  };
};

export const appendANewConversationReducer = (
  state: SessionState,
  action: PayloadAction<ConversationModel>
): SessionState | void => {
  return {
    ...state,
    conversations: [...state.conversations, action.payload],
  };
};

export const appendMoreConversationsReducer = (
  state: SessionState,
  action: PayloadAction<ConversationModel[]>
): SessionState | void => {
  return {
    ...state,
    conversations: [...state.conversations, ...action.payload],
  };
};

export const updateConversationReducer = (
  state: SessionState,
  action: PayloadAction<ConversationModel>
): SessionState | void => {
  const index = state.conversations.findIndex(
    (conversation) => conversation.uuid === action.payload.uuid
  );
  if (index >= 0) {
    const updatedConversation = {
      ...state.conversations[index],
      ...action.payload,
      lastUpdated: new Date(),
    };
    const filteredConversations = state.conversations.filter(
      (conversation) => conversation.uuid !== action.payload.uuid
    );
    return {
      ...state,
      conversations: [...filteredConversations, updatedConversation],
    };
  }
  return state;
};

export const updateNewConversationReducer = (
  state: SessionState,
  action: PayloadAction<ConversationModel>
): SessionState | void => {
  const filteredConversations = state.conversations.filter(
    (conversation) => conversation.uuid !== "new"
  );

  return {
    ...state,
    conversations: [...filteredConversations, { ...action.payload, lastUpdated: new Date() }],
  };
};

export const setShowChatbarReducer = (state: SessionState, action: PayloadAction<boolean>) => {
  state.showChatbar = action.payload;
};

export const toggleChatbarReducer = (state: SessionState) => {
  state.showChatbar = !state.showChatbar;
};

export const setIsMessageStreamingReducer = (
  state: SessionState,
  action: PayloadAction<boolean>
) => {
  state.isMessageStreaming = action.payload;
};
export const setIsLastMessageInProgressReducer = (
  state: SessionState,
  action: PayloadAction<boolean>
) => {
  state.isLastMessageInProgress = action.payload;
};

export const setIsSSEActiveReducer = (state: SessionState, action: PayloadAction<boolean>) => {
  state.isSSEActive = action.payload;
};

export const setIsChatHistoryLoadingReducer = (
  state: SessionState,
  action: PayloadAction<boolean>
) => {
  state.isChatHistoryLoading = action.payload;
};

export const setIsStartedAnalyzingReducer = (
  state: SessionState,
  action: PayloadAction<boolean>
) => {
  state.isStartedAnalyzing = action.payload;
};

export const setCurrentFilterChainIdReducer = (
  state: SessionState,
  action: PayloadAction<string>
) => {
  state.currentFilterChainId = action.payload;
};

export const setIsFilterChainLoadingReducer = (
  state: SessionState,
  action: PayloadAction<boolean>
) => {
  state.isFilterChainLoading = action.payload;
};

export const setFilterChainNodesReducer = (state: SessionState, action: PayloadAction<any[]>) => {
  state.filterChainNodes = action.payload;
};

export const setFilterChainEdgesReducer = (state: SessionState, action: PayloadAction<any[]>) => {
  state.filterChainEdges = action.payload;
};

export const setFilterChainNameReducer = (
  state: SessionState,
  action: PayloadAction<string | null>
) => {
  state.filterChainName = action.payload;
};

export const setCurrentVisualizationReducer = (
  state: SessionState,
  action: PayloadAction<IVisualization | null>
) => {
  state.currentVisualization = action.payload;
};

export const setCurrentTimelineObjectReducer = (
  state: SessionState,
  action: PayloadAction<SessionTimelinePointInfo | null>
) => {
  state.currentTimelineObject = action.payload;
};

export const setReceivedSummaryReducer = (state: SessionState, action: PayloadAction<boolean>) => {
  state.receivedSummary = action.payload;
};

export const setCurrentPromptCycleReducer = (
  state: SessionState,
  action: PayloadAction<MessageModel | null>
) => {
  state.currentPromptCycle = action.payload;
};

//Sets context files to the selectedConversation
export const setSessionContextFileReducer = (
  state: SessionState,
  action: PayloadAction<IContextFile[]>
) => {
  return {
    ...state,
    selectedConversation: {
      ...state.selectedConversation,
      contextFiles: action.payload,
    },
  };
};

export const setSessionSummaryReducer = (
  state: SessionState,
  action: PayloadAction<GetSessionSummaryResponse | null>
) => {
  return {
    ...state,
    sessionSummaryObj: action.payload,
  };
};

export const setFileStatusReducer = (state: SessionState, action: PayloadAction<FILE_STATUS>) => {
  return {
    ...state,
    selectedConversation: {
      ...state.selectedConversation,
      fileStatus: action.payload,
    },
  };
};
